/**
 * The admin-arranged menu layout (GET /api/menu/layout): `sections` (section
 * tabs → category order) and `homeRows` (home quick-link rows), one layout for
 * every store. Falls back to the built-in defaults in utils/menuSections.js
 * until an admin saves a layout.
 *
 * Fetched once per page lifetime (module-level promise, like useFeatureFlags)
 * and mirrored in localStorage, so the first render already has the last-seen
 * layout instead of flashing the defaults. `setMenuLayout` lets the admin editor
 * push a just-saved layout to every mounted screen without a reload.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { readCache, writeCache, CACHE_KEYS } from '../utils/menuCache';
import { MENU_SECTIONS, DEFAULT_HOME_ROWS } from '../utils/menuSections';

const API = process.env.REACT_APP_BACKEND_URL;

let current = null;          // {sections, homeRows, isDefault} once known
let fetchPromise = null;     // dedupes the first fetch across mounts
const listeners = new Set(); // setState of every mounted hook

/**
 * Turn the API / cached payload into what the screens use, filling defaults.
 * @param {{sections?: Array|null, home_rows?: Array|null}|null} raw
 * @returns {{sections: Array, homeRows: Array, isDefault: boolean}}
 */
export function resolveMenuLayout(raw) {
  const sections = Array.isArray(raw?.sections) && raw.sections.length ? raw.sections : null;
  const rows = Array.isArray(raw?.home_rows) ? raw.home_rows : null;
  return {
    sections: sections || MENU_SECTIONS,
    homeRows: rows || DEFAULT_HOME_ROWS,
    isDefault: !sections && !rows,
  };
}

function publish(raw) {
  current = resolveMenuLayout(raw);
  listeners.forEach(fn => fn(current));
}

/**
 * Replace the layout everywhere (after an admin save / reset) and refresh the cache.
 * @param {{sections?: Array|null, home_rows?: Array|null}} raw - the saved payload, or nulls for defaults
 */
export function setMenuLayout(raw) {
  writeCache(CACHE_KEYS.menuLayout, raw || { sections: null, home_rows: null });
  publish(raw);
}

/**
 * The current menu layout: `{sections, homeRows, isDefault}`.
 * @returns {{sections: {name: string, categories: string[]}[], homeRows: {title: string, links: {category: string, label: string}[]}[], isDefault: boolean}}
 */
export function useMenuLayout() {
  const [layout, setLayout] = useState(() => {
    if (!current) current = resolveMenuLayout(readCache(CACHE_KEYS.menuLayout));
    return current;
  });

  useEffect(() => {
    listeners.add(setLayout);
    if (!fetchPromise) {
      fetchPromise = axios.get(`${API}/api/menu/layout`)
        .then(res => {
          writeCache(CACHE_KEYS.menuLayout, { sections: res.data?.sections ?? null, home_rows: res.data?.home_rows ?? null });
          publish(res.data);
        })
        .catch(() => { /* keep the cached / default layout */ });
    }
    if (current) setLayout(current);
    return () => { listeners.delete(setLayout); };
  }, []);

  return layout;
}
